import { useEffect, useRef } from 'react';
import { createLoginShaderRuntime } from './loginShaderRuntime';
import { readShaderPalette } from './loginShaderPalette';
import { FALLBACK_BACKGROUND } from './loginShaderSources';

function getMotionQuery() {
  if (typeof window.matchMedia !== 'function') {
    return {
      matches: false,
      addEventListener: () => {},
      removeEventListener: () => {},
    };
  }

  return window.matchMedia('(prefers-reduced-motion: reduce)');
}

function LoginShaderBackground({ className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return undefined;
    }

    let gl = null;

    try {
      gl = canvas.getContext('webgl', {
        alpha: false,
        antialias: false,
        depth: false,
        stencil: false,
        powerPreference: 'low-power',
      });
    } catch {
      gl = null;
    }

    if (!gl) {
      canvas.style.background = FALLBACK_BACKGROUND;
      return undefined;
    }

    let animationFrame = null;
    const motionQuery = getMotionQuery();
    let reducedMotion = motionQuery.matches;
    let runtime = null;

    try {
      runtime = createLoginShaderRuntime({
        canvas,
        gl,
        palette: readShaderPalette(),
      });
    } catch {
      canvas.style.background = FALLBACK_BACKGROUND;
      return undefined;
    }

    const drawFrame = (timestamp = 0) => {
      runtime.drawFrame(timestamp, { reducedMotion });
    };

    const stop = () => {
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }
    };

    const draw = (timestamp) => {
      animationFrame = null;
      drawFrame(timestamp);

      if (!reducedMotion && !document.hidden) {
        animationFrame = requestAnimationFrame(draw);
      }
    };

    const start = () => {
      if (animationFrame === null && !reducedMotion && !document.hidden) {
        animationFrame = requestAnimationFrame(draw);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
        return;
      }

      drawFrame(performance.now());
      start();
    };

    const handleMotionChange = (event) => {
      reducedMotion = event.matches;

      if (reducedMotion) {
        stop();
        drawFrame(0);
        return;
      }

      start();
    };

    const handleContextLost = (event) => {
      event.preventDefault();
      stop();
    };

    const handlePointerMove = (event) => {
      const timestamp = runtime.handlePointerMove(event);

      if (reducedMotion) {
        drawFrame(timestamp);
      }
    };

    const handlePointerLeave = () => {
      runtime.handlePointerLeave();

      if (reducedMotion) {
        drawFrame(performance.now());
      }
    };

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(() => {
          drawFrame(performance.now());
        })
      : null;

    const interactionTarget = canvas.parentElement || window;

    resizeObserver?.observe(canvas);
    canvas.addEventListener('webglcontextlost', handleContextLost);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    interactionTarget.addEventListener('pointermove', handlePointerMove, { passive: true });
    interactionTarget.addEventListener('pointerleave', handlePointerLeave);
    motionQuery.addEventListener('change', handleMotionChange);

    drawFrame(0);
    start();

    return () => {
      stop();
      resizeObserver?.disconnect();
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      interactionTarget.removeEventListener('pointermove', handlePointerMove);
      interactionTarget.removeEventListener('pointerleave', handlePointerLeave);
      motionQuery.removeEventListener('change', handleMotionChange);
      runtime.dispose();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={className || undefined}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

export default LoginShaderBackground;
